import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageSquare, Loader2, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import PublicHeader from "@/components/shared/PublicHeader";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER || "971545099404";

export default function ContactUsPage() {
    const { lang } = useI18n();

    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const update = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSubmitting(true);
        try {
            await api.post("/public/contact", form);
            setSent(true);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === "string" ? detail : Array.isArray(detail) ? detail[0]?.msg : (lang === "ar" ? "حدث خطأ. يرجى المحاولة لاحقًا." : "Something went wrong. Please try again."));
        } finally { setSubmitting(false); }
    };

    const contactInfo = [
        {
            Icon: Phone,
            label: lang === "ar" ? "هاتف" : "Phone",
            value: "+971 54 509 9404",
            href: `tel:+971545099404`,
        },
        {
            Icon: Mail,
            label: lang === "ar" ? "بريد إلكتروني" : "Email",
            value: "hello@hemaia.net",
            href: "mailto:hello@hemaia.net",
        },
        {
            Icon: MessageSquare,
            label: "WhatsApp",
            value: "+971 54 509 9404",
            href: `https://wa.me/${WHATSAPP}`,
        },
        {
            Icon: MapPin,
            label: lang === "ar" ? "العنوان" : "Address",
            value: lang === "ar"
                ? "شارع الاسراء ٤١ (ميدان لبنان)\nبرج 26 يوليو، الدور 9، مكتب 903\nالمهندسين، الجيزة، مصر"
                : "41 Al-Israa Street (off Lebanon Square)\n26th of July Tower, 9th Floor, Office 903\nMohandessin, Giza, Egypt",
            href: null,
        },
    ];

    return (
        <div className="min-h-screen bg-background" data-testid="contact-us-page">
            <PublicHeader />

            <main className="pt-16">
                {/* Hero — image overlay */}
                <section className="relative py-24 sm:py-28 overflow-hidden">
                    <div className="absolute inset-0">
                        <img
                            src="https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&w=1600"
                            alt=""
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/75 to-primary/60" />
                    </div>
                    <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center text-white">
                        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                            {lang === "ar" ? "تواصل معنا" : "Contact Us"}
                        </h1>
                        <p className="text-lg text-white/85 max-w-2xl mx-auto">
                            {lang === "ar"
                                ? "هل لديك سؤال أو تريد الانضمام إلى شبكتنا؟ فريقنا جاهز لمساعدتك."
                                : "Have a question or want to join our network? Our team is ready to help."}
                        </p>
                    </div>
                </section>

                <section className="py-16">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <div className="grid md:grid-cols-5 gap-10">
                            {/* Contact info */}
                            <div className="md:col-span-2 space-y-6">
                                <h2 className="text-xl font-semibold text-foreground">
                                    {lang === "ar" ? "معلومات التواصل" : "Get In Touch"}
                                </h2>
                                {contactInfo.map(({ Icon, label, value, href }) => (
                                    <div key={label} className="flex gap-4">
                                        <div className="shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Icon className="h-4 w-4 text-primary" />
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-muted-foreground mb-0.5">{label}</div>
                                            {href ? (
                                                <a href={href} className="text-sm font-medium text-foreground hover:text-primary transition-colors" target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noreferrer" : undefined}>
                                                    {value}
                                                </a>
                                            ) : (
                                                <span className="text-sm font-medium text-foreground whitespace-pre-line leading-relaxed">{value}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Form */}
                            <div className="md:col-span-3 surface-card p-6 sm:p-8">
                                {sent ? (
                                    <div className="flex flex-col items-center justify-center py-10 text-center gap-4">
                                        <CheckCircle2 className="h-12 w-12 text-green-500" />
                                        <h3 className="text-xl font-semibold text-foreground">
                                            {lang === "ar" ? "تم إرسال رسالتك!" : "Message Sent!"}
                                        </h3>
                                        <p className="text-sm text-muted-foreground max-w-xs">
                                            {lang === "ar"
                                                ? "شكرًا لتواصلك. سيرد عليك فريقنا خلال 24 ساعة."
                                                : "Thank you for reaching out. Our team will get back to you within 24 hours."}
                                        </p>
                                    </div>
                                ) : (
                                    <form onSubmit={submit} className="space-y-4">
                                        <h2 className="text-xl font-semibold text-foreground mb-6">
                                            {lang === "ar" ? "أرسل لنا رسالة" : "Send Us a Message"}
                                        </h2>

                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1.5">
                                                    {lang === "ar" ? "الاسم" : "Name"} <span className="text-destructive">*</span>
                                                </label>
                                                <input
                                                    name="name" value={form.name} onChange={update} required
                                                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1.5">
                                                    {lang === "ar" ? "البريد الإلكتروني" : "Email"} <span className="text-destructive">*</span>
                                                </label>
                                                <input
                                                    name="email" type="email" value={form.email} onChange={update} required
                                                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1.5">
                                                    {lang === "ar" ? "رقم الهاتف" : "Phone"}
                                                </label>
                                                <input
                                                    name="phone" type="tel" value={form.phone} onChange={update}
                                                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1.5">
                                                    {lang === "ar" ? "الموضوع" : "Subject"}
                                                </label>
                                                <select
                                                    name="subject" value={form.subject} onChange={update}
                                                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                                >
                                                    <option value="">{lang === "ar" ? "اختر موضوعًا" : "Select a subject"}</option>
                                                    <option value="provider">{lang === "ar" ? "الانضمام كمقدم خدمة" : "Join as Provider"}</option>
                                                    <option value="medical_tourism">{lang === "ar" ? "السياحة العلاجية" : "Medical Tourism"}</option>
                                                    <option value="membership">{lang === "ar" ? "استفسار عن العضوية" : "Membership Inquiry"}</option>
                                                    <option value="other">{lang === "ar" ? "أخرى" : "Other"}</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                                {lang === "ar" ? "رسالتك" : "Message"} <span className="text-destructive">*</span>
                                            </label>
                                            <textarea
                                                name="message" value={form.message} onChange={update} required rows={5}
                                                className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                                            />
                                        </div>

                                        {error && (
                                            <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                                                {error}
                                            </div>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={submitting || !form.name || !form.email || !form.message}
                                            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                            {lang === "ar" ? "إرسال الرسالة" : "Send Message"}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
